import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, forwardRef, rem, Text, VStack } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { useCurrentCompany, useCurrentUser, useFeatureFlag } from '@bonusly/who-am-i';

import { useBizyFlightStore, useIsBizyFlightAnchorHidden } from '../../../bizy';
import { SelectorBox, SelectorProps } from '../selector/selector-box';
import { SelectorOption } from '../selector/selector-option';
import { useSelector } from '../selector/use-selector';
import { RecipientBizyGuide } from './recipient-bizy-guide';
import { RecipientOption } from './recipient-option';

const LOADING_TIME_TO_SHOW_MESSAGE = 3000;

export const RecipientSelector = forwardRef<SelectorProps<Recipient>, 'div'>(({ command, hash, items, isLoading, query }, ref) => {
	const { t } = useTranslation(['components', 'recognition']);
	const company = useCurrentCompany();
	const currentUser = useCurrentUser();
	const inviteByEmailEnabled = useFeatureFlag('plg-invite-by-email');
	const isGaugeBizyHidden = useIsBizyFlightAnchorHidden('giveBoxGauge');
	const isBizyParkedAtGiveBox = useBizyFlightStore((state) => state.flight?.phase === 'parked' && state.flight?.destinationAnchorName === 'giveBoxGauge');
	const [showLoadingMessage, setShowLoadingMessage] = useState(false);
	const { getOptionProps, getSelectorProps, selectedIndex } = useSelector({
		command,
		hash,
		items,
		ref,
	});

	useEffect(() => {
		if (isLoading) {
			const timeout = setTimeout(() => {
				setShowLoadingMessage(true);
			}, LOADING_TIME_TO_SHOW_MESSAGE);
			return () => {
				clearTimeout(timeout);
			};
		} else {
			setShowLoadingMessage(false);
		}
	}, [isLoading]);

	const loadingMessage = !showLoadingMessage ? undefined : (
		<Text color="text.softer" fontSize="sm" maxW={72} pt={2} textAlign="center">
			{t('rich_media.loading_is_taking_too_long')}
		</Text>
	);
	const hasNoTeammates = typeof company?.active_user_count === 'number' && company.active_user_count <= 1;
	const showInviteByEmailGuide = inviteByEmailEnabled && ((!!company?.isTrial && !!currentUser?.admin) || hasNoTeammates);
	const canShowBizyGuide = !isGaugeBizyHidden || isBizyParkedAtGiveBox;
	const isBizyRecipient = (recipient: Recipient) =>
		recipient.type === 'username' && (recipient.username?.toLowerCase().includes('bizy') || recipient.displayName?.toLowerCase().includes('bizy'));
	const isBizyOnlyRecipientResult = items.length === 1 && isBizyRecipient(items[0]);
	const showBizyOnlyResultHint = showInviteByEmailGuide && isBizyOnlyRecipientResult && canShowBizyGuide;

	return (
		<SelectorBox data-testid="recipient-selector" isLoading={isLoading} loadingMessage={loadingMessage}>
			<VStack as="ul" alignItems="flex-start" maxH={rem(410)} overflowY="auto" role="listbox" spacing={0} {...getSelectorProps()}>
				{!query && !(showInviteByEmailGuide && canShowBizyGuide && !items.length) && (
					<Box p={2} color="text.softer" fontSize="sm" fontWeight="medium" lineHeight={5}>
						{t('rich_media.search_users.help_text')}
					</Box>
				)}

				{!items.length &&
					(showInviteByEmailGuide && canShowBizyGuide ? (
						<RecipientBizyGuide
							title={query ? t('rich_media.no_results') : t('rich_media.search_users.no_other_users')}
							message={t('rich_media.search_users.enter_work_email_to_invite_new_users')}
						/>
					) : (
						<Box p={2} color="text.softer" fontSize="sm" fontWeight="medium" lineHeight={5}>
							{t('rich_media.no_results')}
						</Box>
					))}

				{items.map((recipient, index) => {
					const showNoOtherUsersHint = showBizyOnlyResultHint && isBizyRecipient(recipient);
					return (
						<SelectorOption isSelected={selectedIndex === index} key={recipient.id} {...getOptionProps(index)}>
							<RecipientOption recipient={recipient} showNoOtherUsersHint={showNoOtherUsersHint} />
						</SelectorOption>
					);
				})}
			</VStack>
		</SelectorBox>
	);
});
